<template>
  <div>
    <div class="px-5">
      <div
        v-if="isBridgeRoute"
        @click="goBack"
        class="flex items-center w-20 gap-x-2 text-gray-90 text-sm cursor-pointer justify-start"
      >
        <i class="el-icon-arrow-left text-xl font-bold"></i>
        <p class="font-light">Back</p>
      </div>
      <div>
        <el-form
          :model="beneficiaryDetailsForm"
          ref="beneficiaryDetailsForm"
          :rules="rules"
          size="normal"
        >
          <el-form-item label="Wallet Tag" prop="tag">
            <el-input
              class="w-full"
              v-model="beneficiaryDetailsForm.tag"
              placeholder="Enter Tag Name"
            ></el-input>
          </el-form-item>
          <el-form-item id="assetSelect" label="Asset Type" prop="assetType">
            <el-select
              @change="getAssetNetworks(beneficiaryDetailsForm.assetType)"
              class="w-full"
              v-model="beneficiaryDetailsForm.assetType"
              placeholder="Select Asset"
            >
              <i slot="prefix">
                <currency-icon
                  :currency="beneficiaryDetailsForm.assetType"
                  class="mt-2.5 mr-10 ml-1"
              /></i>
              <el-option
                v-for="(asset, index) in assets"
                :key="index"
                :label="asset"
                :value="asset"
              >
                <div class="flex items-center">
                  <currency-icon :currency="asset" />
                  <p class="ml-4 uppercase">{{ asset }}</p>
                </div>
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item
            label="Network"
            prop="network"
            v-if="beneficiaryDetailsForm.assetType"
          >
            <el-select
              class="w-full capitalize"
              v-model="beneficiaryDetailsForm.network"
              placeholder="Network"
            >
              <el-option
                v-for="(network, index) in assetNetworks"
                :key="index"
                :label="network.networkShortName"
                :value="network.networkShortName"
                class="mb-2"
              >
                <div class="flex items-center">
                  <network-icon
                    :currency="network.network"
                    size="lg:h-6 lg:w-6 h-4 w-4"
                  />
                  <span class="text-xs ml-4 lg:text-sm">
                    <span class="capitalize">{{
                      `${network.networkShortName}`
                    }}</span>
                    {{ `(${network.code})` }}
                  </span>
                </div></el-option
              >
            </el-select>
          </el-form-item>
          <el-form-item label="Wallet Address" prop="walletAddress">
            <el-input
              class="w-full"
              v-model.trim="beneficiaryDetailsForm.walletAddress"
              placeholder="Enter Wallet Address"
            ></el-input>
          </el-form-item>
          <div class="mt-6">
            <el-button
              :loading="loading"
              @click="validateForm('beneficiaryDetailsForm')"
              class="bg-primary text-white w-full rounded-lg cursor-pointer md:py-4 py-3 md:text-sm text-xs font-light items-center flex justify-center"
            >
              {{ !loading ? 'Next' : 'Confirming....' }}
            </el-button>
          </div>
        </el-form>
      </div>
    </div>
  </div>
</template>

<script>
import assets from '@/mixins/bridge.js'
import {
  GetCurrencyCountry,
  ValidateWallet,
  ValidateInput,
  ValidateSingleSpace
} from '@/utils/helpers.js'
export default {
  mixins: [assets],
  props: {
    loading: {
      type: Boolean,
      default: () => false
    }
  },
  data () {
    return {
      assetNetworks: [],
      rules: {
        tag: [
          {
            required: true,
            message: 'Please enter wallet tag',
            trigger: ['blur', 'change']
          },
          {
            validator: ValidateInput,
            trigger: ['blur', 'change']
          }
        ],
        network: [
          {
            required: true,
            message: 'Please select a network',
            trigger: ['blur', 'change']
          }
        ],
        assetType: [
          {
            required: true,
            message: 'Please select an asset',
            trigger: ['blur', 'change']
          }
        ],
        walletAddress: [
          {
            required: true,
            message: 'Please enter a valid wallet address',
            trigger: 'change'
          },
          {
            validator: this.validateWalletAddress,
            trigger: ['change']
          },
          {
            validator: ValidateSingleSpace,
            trigger: ['change']
          }
        ]
      },
      beneficiaryDetailsForm: {
        assetType: '',
        network: '',
        walletAddress: '',
        tag: ''
      }
    }
  },
  methods: {
    getAssetNetworks (selectedAsset) {
      this.assetNetworks = this.swapAssetsNetworks.filter(
        (asset) => asset.shortName === selectedAsset
      )
    },
    getCurrencyCountry (val) {
      return GetCurrencyCountry(val)
    },
    validateWalletAddress (rule, value, callback) {
      const validator = this.assetNetworks.find(
        (network) =>
          network.networkShortName === this.beneficiaryDetailsForm.network
      ).validator
      const isValid = ValidateWallet(
        value,
        this.beneficiaryDetailsForm.network,
        validator
      )
      if (!isValid) {
        callback(new Error('Invalid Wallet Address'))
      } else {
        callback()
      }
    },
    validateForm (beneficiaryDetailsForm) {
      this.$refs[beneficiaryDetailsForm].validate((valid) => {
        if (valid) {
          this.$store.dispatch(
            'bridge/setBeneficiaryDetailsForm',
            this.beneficiaryDetailsForm
          )
          this.$emit('verifyAuthDialog')
        } else {
          this.$notify.error({
            title: 'Incomplete details',
            message: 'Please fill the missing information',
            position: 'top-right'
          })
        }
      })
    },
    resetForm (form) {
      this.$refs[form].resetFields()
    },
    closeDialog (arg) {
      this[arg] = false
    },
    goBack () {
      this.$emit('goBack')
    },
    closeDrawer () {
      this.$emit('closeDrawer')
    }
  },
  components: {
    CurrencyIcon: () => import('@/assets/icons/CurrencyIcons.vue'),
    NetworkIcon: () => import('@/assets/v2/icons/NetworkIcons.vue')
  },
  computed: {
    routeName () {
      return this.$route.name
    },
    isBridgeRoute () {
      return this.routeName === 'bridge'
    }
  }
}
</script>

<style scoped>
>>> .el-form-item__label {
  font-weight: 300;
  line-height: 20px;
  font-size: 0.8rem;
  padding-top: 18px;
}
>>> .el-form-item {
  margin-bottom: 0px;
}
>>> .el-form-item__error {
  padding-top: 3px;
}
#assetSelect >>> .el-select .el-input__inner {
  text-transform: uppercase;
}
>>> .el-select .el-input__inner {
  text-transform: capitalize;
}
>>> .el-select-dropdown__list {
  padding: 10px;
}
>>> .el-input__inner {
  border-radius: 8px;
  border-color: #c7c8d2;
  height: 48px;
}
</style>
